import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Avatar,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Chip,
  LinearProgress,
  useTheme,
  useMediaQuery,
  Paper,
  Divider,
  Fade,
  Tooltip,
  Switch
} from '@mui/material';
import {
  People,
  CalendarToday,
  TrendingUp,
  AttachMoney,
  MoreVert,
  PersonAdd,
  Event,
  Psychology,
  MonetizationOn,
  Schedule,
  CheckCircle,
  Warning,
  AddCard,
  Visibility,
  VisibilityOff
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { 
  clienteService, 
  agendamentoService, 
  evolucaoService,
  dashboardService,
  recebimentoService, 
  despesaService // Importar o serviço de despesas
} from '../../services/api';

// Interface para agendamentos de hoje
interface TodayAppointment {
  id: number;
  cliente: string;
  horario: string;
  tipo: string;
  status: string;
  foto: string;
}

interface Despesa {
  id: number;
  descricao: string;
  valor: number;
  data_despesa: string;
  tipo_despesa_nome: string;
  data_cadastro: string;
}

const Dashboard: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Log temporário para debug
  console.log(' [DEBUG Dashboard] Dados do usuário:', user);
  console.log(' [DEBUG Dashboard] Nome:', user?.nome);
  console.log(' [DEBUG Dashboard] User_name:', user?.user_name);
  console.log(' [DEBUG Dashboard] user completo:', JSON.stringify(user, null, 2));
  console.log(' [DEBUG Dashboard] user é null?', user === null);
  console.log(' [DEBUG Dashboard] user é undefined?', user === undefined);
  console.log(' [DEBUG Dashboard] user?.nome existe?', !!user?.nome);
  console.log(' [DEBUG Dashboard] Fallback será usado?', !(user?.nome));
  
  // Verificar localStorage também
  const userFromStorage = localStorage.getItem('gestaopsi_user');
  console.log(' [DEBUG Dashboard] localStorage user:', userFromStorage);
  if (userFromStorage) {
    const parsedUser = JSON.parse(userFromStorage);
    console.log(' [DEBUG Dashboard] localStorage parsed:', parsedUser);
    console.log(' [DEBUG Dashboard] localStorage nome:', parsedUser.nome);
  }
  
  // Estados para dados reais
  const [loading, setLoading] = useState(true);
  const [todayAppointments, setTodayAppointments] = useState<TodayAppointment[]>([]); // Tipado corretamente
  const [stats, setStats] = useState([
    {
      title: 'Total de Clientes',
      valueDay: '0',
      valueMonth: '0',
      change: '', 
      changeType: 'positive',
      icon: <People />,
      color: '#1565c0',
      subtitleDay: 'Novos Hoje',
      subtitleMonth: 'Total Ativos'
    },
    {
      title: 'Agendamentos',
      valueDay: '0',
      valueMonth: '0',
      change: '', 
      changeType: 'positive',
      icon: <CalendarToday />,
      color: '#6a1b9a',
      subtitleDay: 'Hoje',
      subtitleMonth: 'Este Mês'
    },
    {
      title: 'Evoluções',
      valueDay: '0',
      valueMonth: '0',
      change: '',
      changeType: 'positive',
      icon: <TrendingUp />,
      color: '#b8935d',
      subtitleDay: 'Registradas Hoje',
      subtitleMonth: 'Este Mês'
    },
    {
      title: 'Recebimentos',
      valueDay: '0',
      valueMonth: '0',
      change: '',
      changeType: 'positive',
      icon: <MonetizationOn />,
      color: '#2e7d32',
      subtitleDay: 'Registros Hoje',
      subtitleMonth: 'Registros Este Mês'
    },
  ]);

  // Estados para despesas
  const [despesas, setDespesas] = useState<Despesa[]>([]);
  const [mostrarValorRecebimentos, setMostrarValorRecebimentos] = useState(false);
  
  // Estados para armazenar dados dos recebimentos para o toggle
  const [dadosRecebimentos, setDadosRecebimentos] = useState({
    recebimentosHoje: [],
    recebimentosMes: [],
    valorRecebimentosHoje: 0,
    valorRecebimentosMes: 0
  });

  // useEffect para atualizar o card Recebimentos quando o toggle muda
  useEffect(() => {
    if (dadosRecebimentos.recebimentosHoje.length > 0 || dadosRecebimentos.recebimentosMes.length > 0) {
      setStats(prevStats => 
        prevStats.map(stat => 
          stat.title === 'Recebimentos' 
            ? {
                ...stat,
                valueDay: mostrarValorRecebimentos 
                  ? `R$ ${dadosRecebimentos.valorRecebimentosHoje.toFixed(2).replace('.', ',')}` 
                  : (dadosRecebimentos.recebimentosHoje?.length || 0).toString(),
                valueMonth: mostrarValorRecebimentos 
                  ? `R$ ${dadosRecebimentos.valorRecebimentosMes.toFixed(2).replace('.', ',')}` 
                  : (dadosRecebimentos.recebimentosMes?.length || 0).toString(),
                subtitleDay: mostrarValorRecebimentos ? 'Hoje' : 'Registros Hoje',
                subtitleMonth: mostrarValorRecebimentos ? 'Este Mês' : 'Registros Este Mês'
              }
            : stat
        )
      );
    }
  }, [mostrarValorRecebimentos, dadosRecebimentos]);

  // Carregar dados reais
  const carregarDadosReais = async () => {
    console.log(' [DASHBOARD] carregarDadosReais INICIADA');
    console.log(' [DASHBOARD] user no carregarDadosReais:', user);
    console.log(' [DASHBOARD] user?.id no carregarDadosReais:', user?.id);
    
    try {
      setLoading(true);
      console.log(' [DASHBOARD] Loading definido como true');
      
      const hoje = new Date();
      const inicioMes = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
      const fimMes = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0);
      const inicioHoje = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate());
      const fimHoje = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate(), 23, 59, 59);

      // Inicializar dados padrão para evitar falhas
      let clientes = [];
      let agendamentos = [];
      let evolucoes = [];
      let recebimentos = [];

      // Buscar clientes com fallback
      try {
        const clientesResponse = await clienteService.listar();
        console.log(' [DEBUG] Resposta completa da API clientes:', clientesResponse);
        clientes = clientesResponse.data?.data || [];
        console.log(' [DEBUG] Clientes carregados:', clientes.length);
      } catch (error) {
        console.error(' [DASHBOARD] Erro ao carregar clientes:', error);
        clientes = [];
      }
      
      const clientesAtivos = clientes.filter((c: any) => {
        return c.ativo === 1 || c.ativo === true || c.ativo === '1';
      });
      
      const clientesHoje = clientes.filter((c: any) => {
        const dataCadastro = new Date(c.data_cadastro);
        const dataCadastroSemHora = new Date(dataCadastro.getFullYear(), dataCadastro.getMonth(), dataCadastro.getDate());
        const dataHojeSemHora = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate());
        return dataCadastroSemHora.getTime() === dataHojeSemHora.getTime();
      });

      // Buscar agendamentos com fallback
      try {
        const agendamentosResponse = await agendamentoService.listar({ id_usuario: user?.id || 1 });
        console.log(' [DEBUG] Resposta agendamentos:', agendamentosResponse);
        agendamentos = agendamentosResponse.data?.data || [];
        console.log(' [DEBUG] Agendamentos carregados:', agendamentos.length);
      } catch (error) {
        console.error(' [DASHBOARD] Erro ao carregar agendamentos:', error);
        agendamentos = [];
      }
      
      const agendamentosHoje = agendamentos.filter((a: any) => {
        // Corrigir problema de fuso horário - usar string diretamente
        const dataAgendamentoStr = a.data; // formato: "YYYY-MM-DD"
        const dataHojeStr = hoje.toISOString().split('T')[0]; // formato: "YYYY-MM-DD"
        console.log(' [DEBUG] Comparando agendamento:', dataAgendamentoStr, 'com hoje:', dataHojeStr);
        return dataAgendamentoStr === dataHojeStr;
      });
      
      console.log(' [DEBUG] Agendamentos de hoje encontrados:', agendamentosHoje.length);
      console.log(' [DEBUG] Lista de agendamentos de hoje:', agendamentosHoje);

      const agendamentosMes = agendamentos.filter((a: any) => {
        const dataAgendamento = new Date(a.data);
        return dataAgendamento >= inicioMes && dataAgendamento <= fimMes;
      });

      // Buscar evoluções com fallback
      try {
        const evolucoesResponse = await evolucaoService.buscarPorUsuario(user?.id);
        console.log(' [DEBUG] Resposta evoluções:', evolucoesResponse);
        evolucoes = evolucoesResponse?.data?.data || [];
        console.log(' [DEBUG] Evoluções carregadas:', evolucoes.length);
      } catch (error) {
        console.error(' [DASHBOARD] Erro ao carregar evoluções:', error);
        evolucoes = [];
      }
      
      // Função para formatar data sem fuso horário
      const formatarDataSemFusoHorario = (dataString: string): Date => {
        if (!dataString) return new Date();
        if (dataString.match(/^\d{4}-\d{2}-\d{2}$/)) {
          const [ano, mes, dia] = dataString.split('-').map(Number);
          return new Date(ano, mes - 1, dia);
        }
        return new Date(dataString);
      };
      
      const evolucoesHoje = evolucoes.filter((e: any) => {
        const dataEvolucao = formatarDataSemFusoHorario(e.data_cadastro);
        const anoEvolucao = dataEvolucao.getFullYear();
        const mesEvolucao = dataEvolucao.getMonth();
        const diaEvolucao = dataEvolucao.getDate();
        const anoHoje = hoje.getFullYear();
        const mesHoje = hoje.getMonth();
        const diaHoje = hoje.getDate();
        return anoEvolucao === anoHoje && mesEvolucao === mesHoje && diaEvolucao === diaHoje;
      });
      
      const evolucoesMes = evolucoes.filter((e: any) => {
        const dataEvolucao = formatarDataSemFusoHorario(e.data_cadastro);
        const anoEvolucao = dataEvolucao.getFullYear();
        const mesEvolucao = dataEvolucao.getMonth();
        const anoHoje = hoje.getFullYear();
        const mesHoje = hoje.getMonth();
        return anoEvolucao === anoHoje && mesEvolucao === mesHoje;
      });

      // Buscar recebimentos com fallback
      try {
        const recebimentosResponse = await recebimentoService.buscarPorUsuario(user?.id);
        console.log(' [DEBUG] Resposta recebimentos:', recebimentosResponse);
        recebimentos = recebimentosResponse?.data?.data || [];
        console.log(' [DEBUG] Recebimentos carregados:', recebimentos.length);
      } catch (error) {
        console.error(' [DASHBOARD] Erro ao carregar recebimentos:', error);
        recebimentos = [];
      }

      // Calcular receita com fallback - FILTRAR POR USUÁRIO LOGADO
      const recebimentosHoje = recebimentos.filter((r: any) => {
        if (!r.data_vencimento || r.id_usuario !== user?.id) return false;
        const dataVencimento = formatarDataSemFusoHorario(r.data_vencimento);
        const anoVencimento = dataVencimento.getFullYear();
        const mesVencimento = dataVencimento.getMonth();
        const diaVencimento = dataVencimento.getDate();
        const anoHoje = hoje.getFullYear();
        const mesHoje = hoje.getMonth();
        const diaHoje = hoje.getDate();
        return anoVencimento === anoHoje && mesVencimento === mesHoje && diaVencimento === diaHoje;
      });

      const recebimentosMes = recebimentos.filter((r: any) => {
        if (!r.data_vencimento || r.id_usuario !== user?.id) return false;
        const dataVencimento = formatarDataSemFusoHorario(r.data_vencimento);
        const anoVencimento = dataVencimento.getFullYear();
        const mesVencimento = dataVencimento.getMonth();
        const anoHoje = hoje.getFullYear();
        const mesHoje = hoje.getMonth();
        return anoVencimento === anoHoje && mesVencimento === mesHoje;
      });

      // Calcular SOMA DOS VALORES dos recebimentos (não apenas pagos)
      const valorRecebimentosHoje = recebimentosHoje.reduce((total: number, r: any) => total + (parseFloat(r.valor) || 0), 0);
      const valorRecebimentosMes = recebimentosMes.reduce((total: number, r: any) => total + (parseFloat(r.valor) || 0), 0);

      // Calcular receita apenas dos recebimentos pagos
      const recebimentosPagosHoje = recebimentosHoje.filter((r: any) => r.status === 'pago');
      const recebimentosPagosMes = recebimentosMes.filter((r: any) => r.status === 'pago');
      
      const receitaHoje = recebimentosPagosHoje.reduce((total: number, r: any) => total + (parseFloat(r.valor) || 0), 0);
      const receitaMes = recebimentosPagosMes.reduce((total: number, r: any) => total + (parseFloat(r.valor) || 0), 0);

      // Atualizar dados dos recebimentos
      setDadosRecebimentos({
        recebimentosHoje,
        recebimentosMes,
        valorRecebimentosHoje,
        valorRecebimentosMes
      });

      // Buscar despesas com fallback
      try {
        const despesasResponse = await despesaService.buscarRecentesPorUsuario(user?.id);
        console.log(' [DEBUG] Resposta despesas:', despesasResponse);
        const despesasRecentes = despesasResponse?.data?.data || [];
        setDespesas(despesasRecentes);
        console.log(' [DEBUG] Despesas carregadas:', despesasRecentes.length);
      } catch (error) {
        console.error(' [DASHBOARD] Erro ao carregar despesas:', error);
        setDespesas([]);
      }

      // Atualizar stats com dados seguros
      setStats([
        {
          title: 'Total de Clientes',
          valueDay: (clientesHoje?.length || 0).toString(),
          valueMonth: (clientesAtivos?.length || 0).toString(),
          change: '', 
          changeType: 'positive',
          icon: <People />,
          color: '#1565c0',
          subtitleDay: 'Novos Hoje',
          subtitleMonth: 'Total Ativos'
        },
        {
          title: 'Agendamentos',
          valueDay: (agendamentosHoje?.length || 0).toString(),
          valueMonth: (agendamentosMes?.length || 0).toString(),
          change: '', 
          changeType: 'positive',
          icon: <CalendarToday />,
          color: '#6a1b9a',
          subtitleDay: 'Hoje',
          subtitleMonth: 'Este Mês'
        },
        {
          title: 'Evoluções',
          valueDay: (evolucoesHoje?.length || 0).toString(),
          valueMonth: (evolucoesMes?.length || 0).toString(),
          change: '',
          changeType: 'positive',
          icon: <TrendingUp />,
          color: '#b8935d',
          subtitleDay: 'Registradas Hoje',
          subtitleMonth: 'Este Mês'
        },
        {
          title: 'Recebimentos',
          valueDay: mostrarValorRecebimentos ? `R$ ${valorRecebimentosHoje.toFixed(2).replace('.', ',')}` : (recebimentosHoje?.length || 0).toString(),
          valueMonth: mostrarValorRecebimentos ? `R$ ${valorRecebimentosMes.toFixed(2).replace('.', ',')}` : (recebimentosMes?.length || 0).toString(),
          change: '',
          changeType: 'positive',
          icon: <MonetizationOn />,
          color: '#2e7d32',
          subtitleDay: mostrarValorRecebimentos ? 'Hoje' : 'Registros Hoje',
          subtitleMonth: mostrarValorRecebimentos ? 'Este Mês' : 'Registros Este Mês'
        },
      ]);

      // Atualizar agendamentos de hoje com fallback
      const sortedAppointments = (agendamentosHoje || []).sort((a: any, b: any) => {
        const horaA = a.hora.split(':').map(Number);
        const horaB = b.hora.split(':').map(Number);
        return horaA[0] - horaB[0] || horaA[1] - horaB[1];
      });
      setTodayAppointments(sortedAppointments.map((agendamento: any) => ({
        id: agendamento.id || 0,
        cliente: agendamento.cliente_nome || 'Cliente não informado',
        horario: (agendamento.hora || '00:00').substring(0, 5), // Formatar para HH:MM
        tipo: agendamento.tipo || 'Consulta',
        status: agendamento.status || 'Agendado',
        foto: agendamento.cliente_foto || '',
      })));

      console.log(' [DASHBOARD] Dados carregados com sucesso');

    } catch (error) {
      console.error(' [DASHBOARD] Erro crítico ao carregar dados:', error);
      
      // Definir dados padrão em caso de erro total
      setStats([
        {
          title: 'Total de Clientes',
          valueDay: '0',
          valueMonth: '0',
          change: '', 
          changeType: 'positive',
          icon: <People />,
          color: '#1565c0',
          subtitleDay: 'Novos Hoje',
          subtitleMonth: 'Total Ativos'
        },
        {
          title: 'Agendamentos',
          valueDay: '0',
          valueMonth: '0',
          change: '', 
          changeType: 'positive',
          icon: <CalendarToday />,
          color: '#6a1b9a',
          subtitleDay: 'Hoje',
          subtitleMonth: 'Este Mês'
        },
        {
          title: 'Evoluções',
          valueDay: '0',
          valueMonth: '0',
          change: '',
          changeType: 'positive',
          icon: <TrendingUp />,
          color: '#b8935d',
          subtitleDay: 'Registradas Hoje',
          subtitleMonth: 'Este Mês'
        },
        {
          title: 'Recebimentos',
          valueDay: '0',
          valueMonth: '0',
          change: '',
          changeType: 'positive',
          icon: <MonetizationOn />,
          color: '#2e7d32',
          subtitleDay: 'Registros Hoje',
          subtitleMonth: 'Registros Este Mês'
        },
      ]);
      
      setTodayAppointments([]);
      setDespesas([]);
    } finally {
      setLoading(false);
      console.log(' [DASHBOARD] Loading finalizado');
    }
  };

  useEffect(() => {
    console.log(' [DASHBOARD] useEffect executado, user:', user);
    console.log(' [DASHBOARD] user?.id:', user?.id);
    
    if (user?.id) {
      console.log(' [DASHBOARD] Chamando carregarDadosReais...');
      carregarDadosReais();
    } else {
      console.log(' [DASHBOARD] user?.id não encontrado, não carregando dados');
    }
  }, [user]);

  const getStatusColor = (status: string): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
    switch (status) {
      case 'Ativo':
      case 'Confirmado':
        return 'success';
      case 'Pendente':
        return 'warning';
      case 'Inativo':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string): React.ReactElement => {
    switch (status) {
      case 'Confirmado':
        return <CheckCircle fontSize="small" />;
      case 'Pendente':
        return <Warning fontSize="small" />;
      default:
        return <Schedule fontSize="small" />;
    }
  };

  return (
    <Box sx={{ p: isMobile ? 2 : 3 }}>
      {/* Header de Boas-vindas */}
      <Fade in timeout={500}>
        <Box sx={{ mb: isMobile ? 3 : 4 }}>
          {/* Header com título e ícones na mesma linha */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            mb: 1
          }}>
            <Typography 
              variant={isMobile ? "h5" : "h4"} 
              sx={{ 
                fontWeight: 600,
                background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}
            >
              Olá, {(user?.user_name || 'Usuário').split(' ')[0]}! 👋
            </Typography>
            
            {/* Ações Rápidas - Ícones Alinhados à Direita */}
            <Box sx={{ 
              display: 'flex', 
              gap: 1
            }}>
              <Tooltip title="Novo Cliente" arrow>
                <IconButton
                  onClick={() => navigate('/app/clientes')}
                  sx={{ 
                    bgcolor: theme.palette.primary.main,
                    color: 'white',
                    '&:hover': {
                      bgcolor: theme.palette.primary.dark,
                      transform: 'scale(1.1)',
                    },
                    transition: 'all 0.2s ease-in-out'
                  }}
                >
                  <PersonAdd />
                </IconButton>
              </Tooltip>
              
              <Tooltip title="Agendamentos" arrow>
                <IconButton
                  onClick={() => navigate('/app/agendamento-calendario')}
                  sx={{ 
                    bgcolor: theme.palette.secondary.main,
                    color: 'white',
                    '&:hover': {
                      bgcolor: theme.palette.secondary.dark,
                      transform: 'scale(1.1)',
                    },
                    transition: 'all 0.2s ease-in-out'
                  }}
                >
                  <Event />
                </IconButton>
              </Tooltip>
              
              <Tooltip title="Nova Evolução" arrow>
                <IconButton
                  onClick={() => navigate('/app/evolucoes')}
                  sx={{ 
                    bgcolor: theme.palette.info.main,
                    color: 'white',
                    '&:hover': {
                      bgcolor: theme.palette.info.dark,
                      transform: 'scale(1.1)',
                    },
                    transition: 'all 0.2s ease-in-out'
                  }}
                >
                  <Psychology />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          
          <Typography variant="body1" color="text.secondary">
            Aqui está um resumo das suas atividades hoje.
          </Typography>
        </Box>
      </Fade>

      {/* Cards de Estatísticas */}
      <Grid container spacing={isMobile ? 2 : 3} sx={{ mb: isMobile ? 3 : 4 }}>
        {stats.map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Fade in timeout={600 + (index * 100)}>
              <Card
                sx={{
                  height: '100%',
                  background: `linear-gradient(135deg, ${stat.color}15, ${stat.color}05)`,
                  border: `1px solid ${stat.color}20`,
                  transition: 'all 0.3s ease-in-out',
                  borderRadius: 3,
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: `0 8px 25px ${stat.color}25`,
                  },
                }}
              >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Avatar
                    sx={{
                      bgcolor: stat.color,
                      width: 48,
                      height: 48,
                    }}
                  >
                    {stat.icon}
                  </Avatar>
                  {stat.title === 'Recebimentos' ? (
                    <Tooltip title={mostrarValorRecebimentos ? "Mostrar quantidade" : "Mostrar valores"} arrow>
                      <IconButton 
                        size="small"
                        onClick={() => setMostrarValorRecebimentos(!mostrarValorRecebimentos)}
                        sx={{ 
                          color: stat.color,
                          '&:hover': {
                            bgcolor: `${stat.color}15`,
                          }
                        }}
                      >
                        {mostrarValorRecebimentos ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                  ) : stat.title === 'Total de Clientes' || stat.title === 'Agendamentos' || stat.title === 'Evoluções' ? (
                    // Não mostrar ícone para estes cards
                    null
                  ) : (
                    <IconButton size="small">
                      <MoreVert />
                    </IconButton>
                  )}
                </Box>
                
                {/* Título do Card */}
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: stat.color }}>
                  {stat.title}
                </Typography>
                
                {/* Valor do Dia - Destaque */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
                    {stat.valueDay}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {stat.subtitleDay}
                  </Typography>
                </Box>
                
                {/* Valor Mensal - Secundário */}
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: stat.title === 'Total de Clientes' || stat.title === 'Agendamentos' || stat.title === 'Evoluções' || stat.title === 'Recebimentos' || stat.title === 'Receita' ? 'flex-end' : 'space-between',
                  p: 1.5,
                  bgcolor: `${stat.color}08`,
                  borderRadius: 1,
                  border: `1px solid ${stat.color}15`,
                  width: '100%'
                }}>
                  <Box sx={{ 
                    textAlign: stat.title === 'Total de Clientes' || stat.title === 'Agendamentos' || stat.title === 'Evoluções' || stat.title === 'Recebimentos' || stat.title === 'Receita' ? 'right' : 'left',
                    width: stat.title === 'Total de Clientes' || stat.title === 'Agendamentos' || stat.title === 'Evoluções' || stat.title === 'Recebimentos' || stat.title === 'Receita' ? '100%' : 'auto'
                  }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: stat.color }}>
                      {stat.valueMonth}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {stat.subtitleMonth}
                    </Typography>
                  </Box>
                  {stat.change && (
                    <Chip
                      label={stat.change}
                      size="small"
                      color={stat.changeType === 'positive' ? 'success' : 'error'}
                      variant="outlined"
                    />
                  )}
                </Box>
              </CardContent>
              </Card>
            </Fade>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={isMobile ? 2 : 3}>
        {/* Consultas de Hoje */}
        <Grid item xs={12} lg={6}>
          <Fade in timeout={800}>
            <Card sx={{ 
              height: '100%',
              borderRadius: 3,
              boxShadow: 3
            }}>
              <CardContent sx={{ p: isMobile ? 2 : 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                  <Typography 
                    variant={isMobile ? "subtitle1" : "h6"} 
                    sx={{ fontWeight: 600 }}
                  >
                    📅 Atendimentos de Hoje
                  </Typography>
                  <Chip
                    icon={<Event />}
                    label={`${todayAppointments.length} agendadas`}
                    color="primary"
                    variant="outlined"
                  />
                </Box>
              
              <List>
                {todayAppointments.map((appointment, index) => (
                  <React.Fragment key={appointment.id}>
                    <ListItem
                      sx={{
                        borderRadius: 2,
                        mb: 1,
                        bgcolor: 'background.default',
                        '&:hover': {
                          bgcolor: 'action.hover',
                        },
                      }}
                    >
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                              {appointment.cliente}
                            </Typography>
                            <Chip
                              icon={getStatusIcon(appointment.status)}
                              label={appointment.status}
                              size="small"
                              color={getStatusColor(appointment.status)}
                              variant="outlined"
                            />
                          </Box>
                        }
                        secondary={
                          <Box sx={{ mt: 0.5 }}>
                            <Typography variant="body2" color="text.secondary">
                              {appointment.horario} - {appointment.tipo}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                    {index < todayAppointments.length - 1 && <Divider variant="inset" component="li" />}
                  </React.Fragment>
                ))}
              </List>
              </CardContent>
            </Card>
          </Fade>
        </Grid>

        {/* Despesas */}
        <Grid item xs={12} lg={6}>
          <Fade in timeout={1000}>
            <Card sx={{ 
              height: '100%',
              borderRadius: 3,
              boxShadow: 3
            }}>
              <CardContent sx={{ p: isMobile ? 2 : 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                  <Typography 
                    variant={isMobile ? "subtitle1" : "h6"} 
                    sx={{ fontWeight: 600 }}
                  >
                    👥 Despesas Recentes
                  </Typography>
                  <IconButton 
                    size="small"
                    onClick={() => navigate('/app/despesas')}
                    sx={{ 
                      bgcolor: theme.palette.primary.main,
                      color: 'white',
                      '&:hover': {
                        bgcolor: theme.palette.primary.dark,
                      }
                    }}
                  >
                    <AddCard />
                  </IconButton>
                </Box>
              
              <List>
                {despesas.map((despesa, index) => (
                  <React.Fragment key={despesa.id}>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: theme.palette.secondary.main }}>
                          {despesa.descricao?.charAt(0) || 'D'}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: theme.palette.error.main }}>
                              {despesa.tipo_despesa_nome || 'Tipo não informado'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontWeight: 600 }}>
                              {despesa.descricao || 'Sem descrição'}
                            </Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5 }}>
                              <Typography variant="body2" color="text.secondary">
                                Data: {(() => {
                                  if (!despesa.data_despesa) return '';
                                  let data = despesa.data_despesa;
                                  if (data.includes('T')) {
                                    data = data.split('T')[0];
                                  }
                                  const [ano, mes, dia] = data.split('-');
                                  const dataLocal = new Date(parseInt(ano), parseInt(mes) - 1, parseInt(dia));
                                  return dataLocal.toLocaleDateString('pt-BR');
                                })()}
                              </Typography>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: theme.palette.error.main }}>
                                Valor: R$ {Number(despesa.valor || 0).toFixed(2).replace('.', ',')}
                              </Typography>
                            </Box>
                          </Box>
                        }
                      />
                    </ListItem>
                    {index < despesas.length - 1 && <Divider variant="inset" component="li" />}
                  </React.Fragment>
                ))}
                {despesas.length === 0 && (
                  <ListItem>
                    <ListItemText
                      primary={
                        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                          Nenhuma despesa encontrada
                        </Typography>
                      }
                    />
                  </ListItem>
                )}
              </List>
              </CardContent>
            </Card>
          </Fade>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
